import {AnchorExternal, RootLayout, RsnButton, RsnDialog, styles} from '@raison/ui-kit';
import {Atom, useAtomValue} from 'jotai';
import React, {Fragment} from 'react';
import {Link} from 'react-router';
import {getPagesContent} from './content';
import * as cornerDecorCss from './css/cornerDecor.css';
import * as featureListCss from './css/featureList.css';
import {TariffDescriptionProps, TariffFlag, TariffName} from './types';

export type TariffService = {
    isPending: boolean;
    isSuccess: boolean;
    reset: () => void;
    mutate: (tariffName: TariffName) => void;
};

type TariffDescriptionScreenProps = {
    title: TariffName;
    activeTariff: TariffName;
    tariffService: Atom<TariffService>;
};

export function TariffDescriptionScreen({activeTariff, title, tariffService}: TariffDescriptionScreenProps) {
    const isActive = activeTariff === title;

    const {
        isSuccess: requestSuccessful,
        isPending: requestPending,
        reset,
        mutate: request,
    } = useAtomValue(tariffService);

    const {
        attributes: {[title]: shortDescriptions},
        descriptions: {[title]: descriptions},
        dialogs: {[title]: dialogText},
        texts,
    } = getPagesContent();

    const {subtitle, flags} = shortDescriptions;
    const notAvailableYet = flags.includes(TariffFlag.ComingSoon);

    let onTariffRequest = () => request(title);
    if (notAvailableYet) {
        onTariffRequest = noop;
    }
    const onDialogClose = () => reset();

    return (
        <RootLayout title={title}>
            <div className='relative z-10 flex flex-1 flex-col gap-6 p-4'>
                <div className={cls.decoration[title]} />

                <hgroup className='grid gap-1'>
                    {isActive ? (
                        <h2 className={cls.title}>
                            {`${title} plan`} <br /> {'is active'}
                        </h2>
                    ) : (
                        <Fragment>
                            <h2 className={cls.title}>{title}</h2>
                            <small className={cls.subtitle}>{subtitle}</small>
                        </Fragment>
                    )}
                </hgroup>

                {isActive ? (
                    <TariffDescription {...descriptions.active} />
                ) : (
                    <TariffDescription {...descriptions.inactive} />
                )}

                <div className='mt-auto'>
                    {isActive ? (
                        <Link to='/assets'>
                            <RsnButton.Primary>Back to my accounts</RsnButton.Primary>
                        </Link>
                    ) : title === 'Wealth' ? (
                        <AnchorExternal href='https://calendly.com/o-raim-raison'>
                            <RsnButton.Primary>Request</RsnButton.Primary>
                        </AnchorExternal>
                    ) : (
                        <RsnButton.Primary onClick={onTariffRequest} disabled={notAvailableYet || requestPending}>
                            {notAvailableYet ? texts.ComingSoon : texts.Request}
                        </RsnButton.Primary>
                    )}
                </div>
            </div>

            <RsnDialog.Controller isOpen={requestSuccessful} onClose={onDialogClose}>
                <RsnDialog.Window placement='center'>
                    <RsnDialog.CenteredHeader>Request accepted</RsnDialog.CenteredHeader>
                    <RsnDialog.Body>{dialogText.requestAccepted}</RsnDialog.Body>
                    <RsnDialog.Footer>
                        <RsnButton.Secondary onClick={onDialogClose}>I got it</RsnButton.Secondary>
                    </RsnDialog.Footer>
                </RsnDialog.Window>
            </RsnDialog.Controller>
        </RootLayout>
    );
}

function TariffDescription({textContent, featureList}: TariffDescriptionProps) {
    return (
        <div className='flex flex-col gap-6'>
            {textContent != null && <div className='fv-text-l text-neutral-90'>{textContent}</div>}

            {featureList && featureList.length !== 0 && (
                <ul className={cls.featureItem}>
                    {featureList.map((feature, i) => (
                        <li className={`flex gap-2 ${cls.checkMark}`} key={i}>
                            {feature}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

const cls = styles({
    ...cornerDecorCss,
    ...featureListCss,
    featureItem: 'fv-text-l flex list-none flex-col gap-2 text-neutral-100',
    title: 'fv-heading-l text-neutral-100',
    subtitle: 'fv-text-xl-regular text-neutral-60',
});

const noop = () => {};
